import React from 'react';
import { BarChart3, Check, Copy, ExternalLink, Link as LinkIcon, Monitor, ShieldCheck, UserCog, Users, WalletCards } from 'lucide-react';

const PROD_LINKS = [
  {
    id: 'cashier',
    title: 'Экран кассира',
    url: 'https://app.jpexgo.com/cashier',
    description: 'Вход по PIN, выдача заказов.',
    icon: <ShieldCheck size={18} />,
  },
  {
    id: 'pickup',
    title: 'Экран клиента',
    url: 'https://app.jpexgo.com/pickup',
    description: 'POS-моноблок, код выдачи и QR-оплата.',
    icon: <Monitor size={18} />,
  },
  {
    id: 'manager',
    title: 'Кабинет менеджера',
    url: 'https://manager.jpexgo.com',
    description: 'Главный вход для менеджера/админа.',
    icon: <LinkIcon size={18} />,
  },
  {
    id: 'reports',
    title: 'Отчёты',
    url: 'https://manager.jpexgo.com/manager/reports',
    description: 'Оплаты, выдачи, ожидают оплаты, кассиры.',
    icon: <BarChart3 size={18} />,
  },
  {
    id: 'reconciliation',
    title: 'Сверка с банком',
    url: 'https://manager.jpexgo.com/manager/reconciliation',
    description: 'Выписка Optima и сравнение с базой.',
    icon: <WalletCards size={18} />,
  },
  {
    id: 'cashiers-admin',
    title: 'Админка кассиров',
    url: 'https://manager.jpexgo.com/admin/cashiers',
    description: 'Кассиры, PIN-коды, отключение.',
    icon: <Users size={18} />,
  },
  {
    id: 'staff-admin',
    title: 'Сотрудники и роли',
    url: 'https://manager.jpexgo.com/admin/staff',
    description: 'Админы, менеджеры, операторы и страны доступа.',
    icon: <UserCog size={18} />,
  },
];

const copyText = async (text) => {
  const value = String(text || '').trim();
  if (!value) return false;

  const tgClipboard = window?.Telegram?.WebApp?.writeTextToClipboard;
  if (typeof tgClipboard === 'function') {
    await new Promise((resolve) => {
      tgClipboard(value, () => resolve());
    });
    return true;
  }

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(textarea);
  return ok;
};

const AdminLinksPage = () => {
  const [copiedId, setCopiedId] = React.useState('');

  const handleOpen = (url) => {
    const value = String(url || '').trim();
    if (!value) return;

    const tgOpenLink = window?.Telegram?.WebApp?.openLink;
    if (typeof tgOpenLink === 'function') {
      tgOpenLink(value);
      return;
    }

    window.open(value, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async (item) => {
    const ok = await copyText(item.url);
    if (!ok) return;
    setCopiedId(item.id);
    window.setTimeout(() => setCopiedId(''), 1600);
  };

  return (
    <div className="min-h-screen bg-bg px-4 py-5 text-text-primary">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-3xl border border-stroke bg-surface p-4 shadow-[0_18px_70px_rgba(0,0,0,0.24)]">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-neon-yellow/25 bg-neon-yellow/10 px-3 py-1 text-xs font-semibold text-neon-yellow">
                <LinkIcon size={15} /> Служебные ссылки
              </div>
              <h1 className="mt-2 text-xl font-black tracking-tight">Ссылки для работы</h1>
            </div>
            <p className="max-w-md text-sm text-text-secondary">
              Рабочие prod-адреса для POS-моноблока, менеджера и админки.
            </p>
          </div>
        </div>

        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {PROD_LINKS.map((item) => {
            const copied = copiedId === item.id;
            return (
              <div key={item.id} className="rounded-2xl border border-stroke bg-surface p-3 shadow-[0_10px_32px_rgba(0,0,0,0.18)]">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-neon-yellow">
                    {item.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h2 className="text-sm font-black">{item.title}</h2>
                        <p className="mt-0.5 text-xs text-text-secondary">{item.description}</p>
                      </div>
                    </div>
                    <div className="mt-2 rounded-xl border border-white/10 bg-black/20 px-2.5 py-2 font-mono text-xs text-neon-blue break-all">
                      {item.url}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleCopy(item)}
                        className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-xl border border-neon-yellow/30 bg-neon-yellow/10 px-3 text-xs font-bold text-neon-yellow transition hover:bg-neon-yellow/15"
                      >
                        {copied ? <Check size={15} /> : <Copy size={15} />}
                        {copied ? 'Скопировано' : 'Копировать'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpen(item.url)}
                        className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-xl border border-stroke bg-white/5 px-3 text-xs font-bold text-text-primary transition hover:bg-white/10"
                      >
                        <ExternalLink size={15} />
                        Открыть
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 rounded-2xl border border-neon-blue/20 bg-neon-blue/10 p-3 text-xs text-text-secondary">
          Для клиентского экрана на POS-моноблоке используйте ссылку “Экран клиента” и запускайте Chrome в kiosk mode.
        </div>
      </div>
    </div>
  );
};

export default AdminLinksPage;
